/**
 * GiftRedeemPage — `/gift/redeem`.
 *
 * Auth-required (bounces unauth users to `/login?next=/gift/redeem`).
 * User enters a 12-char redemption code. On success:
 *
 *   • Credit gift → recipient's PooBucks balance is $inc'd via the
 *     shared `_write_ledger_entry_and_inc_balance` helper. On success
 *     we call `refreshMe()` so the AvatarDropdown / dashboard chip
 *     picks up the new balance without a hard reload.
 *
 *   • Service gift → the gift row is marked consumed; the offering
 *     is displayed to the recipient (applying it to a booking is
 *     Stripe-phase work, see grep marker in `backend/gifts.py`:
 *     `TODO (Stripe phase): apply gifted service to booking/payout`).
 *
 * Error taxonomy (from the backend redeem handler):
 *   401 → prompt to log in (interceptor handles this via bounce)
 *   404 → "invalid_code"          → "That code doesn't match a gift."
 *   400 → "gift_not_paid"          → "That gift hasn't finished checkout."
 *   409 → "already_redeemed"       → "That gift's already been redeemed."
 *   410 → "expired"                → "That gift's expired."
 *   500 → generic "Something went wrong."
 *
 * Copy tone: friendly + encouraging on success, matter-of-fact on
 * errors (no shame framing per dispatch).
 */
import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { Loader2, Gift, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import FloatingThemeToggle from "@/components/FloatingThemeToggle";

const ERROR_COPY = {
  invalid_code: "That code doesn't match a gift. Double-check for typos.",
  gift_not_paid: "That gift hasn't finished checkout yet. Give it a minute and try again.",
  already_redeemed: "That gift's already been redeemed. Codes are one-time use.",
  expired: "That gift's expired. Gifts last 12 months from the day they're bought.",
};

export default function GiftRedeemPage() {
  const location = useLocation();
  const { user, refreshMe } = useAuth();
  const { creditLabel } = useConfig();

  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Preserve pre-fill (`?code=XXX`) for the "email link → redeem" flow
  // once the delivery-method dispatch lands. Grep marker in gifts.py:
  // `TODO: delivery method — code-entry vs email auto-apply (undecided)`.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get("code");
    if (fromUrl) setCode(fromUrl.trim().toUpperCase());
  }, [location.search]);

  if (user === null) {
    // Not authed — bounce to login with a `next` param so we return here.
    return <Navigate to={`/login?next=${encodeURIComponent("/gift/redeem")}`} replace />;
  }

  async function handleRedeem(e) {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      toast.error("Enter a redemption code to continue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/gifts/redeem", { redemption_code: trimmed });
      setResult({ ok: true, data });
      // Refresh the auth context so the dashboard PooBucks chip picks
      // up the newly-incremented balance without a page reload.
      if (data?.gift?.gift_type === "credit") {
        refreshMe?.();
      }
      toast.success("Gift redeemed!");
    } catch (err) {
      const errPayload = err?.response?.data;
      const errorKey = errPayload?.error;
      const detail = errPayload?.detail;
      const friendly = ERROR_COPY[errorKey] || formatApiErrorDetail(detail) || err.message;
      setResult({ ok: false, error: errorKey || "unknown", message: friendly });
      toast.error(friendly);
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    setResult(null);
    setCode("");
  }

  if (result?.ok) {
    const { data } = result;
    const gift = data.gift || {};
    const isCredit = gift.gift_type === "credit";
    return (
      <div className="min-h-screen bg-pp-surface2" data-testid="gift-redeem-page-success">
        <div className="mx-auto max-w-2xl px-6 py-16">
          <Card className="border-brand-orange/20 bg-pp-surface p-10 text-center">
            <div className="mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-orange/10">
              <CheckCircle2 className="h-8 w-8 text-brand-orange" aria-hidden="true" />
            </div>
            <h1
              className="font-heading text-3xl font-bold text-pp-text sm:text-4xl"
              data-testid="gift-redeem-success-heading"
            >
              You&apos;ve been gifted a PooPluck!
            </h1>
            {isCredit ? (
              <>
                <p
                  className="mt-3 text-base text-pp-text-muted"
                  data-testid="gift-redeem-credit-message"
                >
                  {`$${Number(gift.amount_cents / 100).toFixed(2)} in ${creditLabel} landed in your account.`}
                </p>
                <div className="mt-6 inline-flex items-baseline gap-2 rounded-full bg-pp-surface2 px-5 py-3">
                  <span className="text-[11px] font-bold uppercase tracking-[0.10em] text-pp-text-muted">
                    New balance
                  </span>
                  <span
                    className="font-heading text-xl font-bold tabular-nums text-brand-orange"
                    data-testid="gift-redeem-new-balance"
                  >
                    ${Number(data.new_balance || 0).toFixed(2)}
                  </span>
                </div>
              </>
            ) : (
              <p
                className="mt-3 text-base text-pp-text-muted"
                data-testid="gift-redeem-service-message"
              >
                {`Enjoy: "${data.offering?.offering_label || gift.offering_label || "your gifted service"}"`}
                .
              </p>
            )}
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Button
                type="button"
                onClick={reset}
                variant="outline"
                data-testid="gift-redeem-reset-btn"
              >
                Redeem another
              </Button>
              <Button asChild data-testid="gift-redeem-dashboard-btn">
                <Link to="/dashboard/customer">Go to my dashboard</Link>
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-pp-surface2" data-testid="gift-redeem-page">
      <FloatingThemeToggle />
      <div className="mx-auto max-w-lg px-6 py-16">
        <div className="mb-8 text-left">
          <div className="mb-3 inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-orange/10">
            <Gift className="h-7 w-7 text-brand-orange" aria-hidden="true" />
          </div>
          <h1
            className="font-heading text-4xl font-bold text-pp-text sm:text-5xl"
            data-testid="gift-redeem-heading"
          >
            Redeem your gift.
          </h1>
          <p className="mt-3 text-lg text-pp-text-muted">
            Someone loves you enough to gift you a PooPluck. Drop your code in
            below and we&apos;ll take care of the rest.
          </p>
        </div>

        <Card className="border-pp-border bg-pp-surface p-8">
          <form onSubmit={handleRedeem} className="space-y-5" data-testid="gift-redeem-form">
            <div>
              <Label htmlFor="gift-code-input" className="mb-2 block text-sm font-semibold text-pp-text">
                Redemption code
              </Label>
              <Input
                id="gift-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="XXXXXXXXXXXX"
                maxLength={12}
                autoComplete="off"
                className="font-heading text-center text-2xl tracking-[0.20em]"
                data-testid="gift-redeem-code-input"
              />
              <p className="mt-2 text-xs text-pp-text-muted">
                12 characters. We ignore case.
              </p>
            </div>

            {result?.ok === false && (
              <div
                className="rounded-lg border border-brand-orange/30 bg-brand-orange/5 px-4 py-3 text-sm text-pp-text"
                data-testid="gift-redeem-error"
                data-error-code={result.error}
                role="alert"
              >
                {result.message}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-orange text-white hover:bg-brand-orangeDark"
              data-testid="gift-redeem-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  Redeeming…
                </>
              ) : (
                "Redeem gift"
              )}
            </Button>

            <p className="text-center text-xs text-pp-text-muted">
              Want to gift a PooPluck instead?{" "}
              <Link
                to="/gift/buy"
                className="font-semibold text-brand-orange underline-offset-2 hover:underline"
                data-testid="gift-redeem-goto-buy"
              >
                Buy one here
              </Link>
              .
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
}
